import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, fmtNumber, fmtMoney, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, CircleNotch, AddressBook, Package } from "@phosphor-icons/react";

const EMPTY = { nombre: "", email: "", telefono: "", condiciones: "", notas: "" };

export default function Proveedores() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [lotesProv, setLotesProv] = useState(null);
  const [lotesData, setLotesData] = useState(null);

  const openLotes = (p) => {
    setLotesProv(p);
    setLotesData(null);
    api.get(`/lotes?proveedor_id=${p.id}`).then((r) => setLotesData(r.data));
  };

  const load = useCallback(() => {
    api.get("/proveedores").then((r) => setRows(r.data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setError("");
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      nombre: p.nombre || "",
      email: p.email || "",
      telefono: p.telefono || "",
      condiciones: p.condiciones || "",
      notas: p.notas || "",
    });
    setError("");
    setOpen(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/proveedores/${editing.id}`, form);
        toast.success("Proveedor actualizado");
      } else {
        await api.post("/proveedores", form);
        toast.success("Proveedor creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/proveedores/${toDelete.id}`);
      toast.success("Proveedor eliminado");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="proveedores-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-md bg-[#EFF6FF] flex items-center justify-center">
            <AddressBook size={22} weight="bold" className="text-[#1E40AF]" />
          </span>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Proveedores</h1>
            <p className="text-[#475569] mt-1">Agenda de proveedores. Se usan al crear lotes y al generar órdenes de compra.</p>
          </div>
        </div>
        <Button data-testid="new-proveedor-button" onClick={openNew} className="h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold">
          <Plus size={18} weight="bold" className="mr-1.5" /> Nuevo proveedor
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
        {rows === null ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={28} className="animate-spin text-[#0F172A]" />
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <AddressBook size={40} weight="duotone" className="mx-auto text-[#94A3B8] mb-3" />
            <p className="text-[#64748B]">Aún no hay proveedores. Crea el primero.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="proveedores-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                  <th className="px-5 py-3 font-semibold">Nombre</th>
                  <th className="px-5 py-3 font-semibold">Email</th>
                  <th className="px-5 py-3 font-semibold">Teléfono</th>
                  <th className="px-5 py-3 font-semibold">Condiciones</th>
                  <th className="px-5 py-3 font-semibold text-right">Lotes</th>
                  <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} data-testid={`proveedor-row-${p.id}`} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                    <td className="px-5 py-3 font-semibold text-[#0F172A]">{p.nombre}</td>
                    <td className="px-5 py-3 text-[#475569]">{p.email || "—"}</td>
                    <td className="px-5 py-3 text-[#475569]">{p.telefono || "—"}</td>
                    <td className="px-5 py-3 text-[#475569]">{p.condiciones || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      {p.num_lotes > 0 ? (
                        <button
                          data-testid={`proveedor-lotes-${p.id}`}
                          onClick={() => openLotes(p)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#EFF6FF] text-[#1E40AF] text-xs font-bold hover:bg-[#DBEAFE] transition-colors"
                          title="Ver lotes"
                        >
                          <Package size={14} weight="bold" /> {fmtNumber(p.num_lotes)}
                        </button>
                      ) : (
                        <span className="text-xs text-[#94A3B8]">0</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`edit-proveedor-${p.id}`} onClick={() => openEdit(p)} className="p-2 rounded-md text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]" title="Editar">
                          <PencilSimple size={18} />
                        </button>
                        <button data-testid={`delete-proveedor-${p.id}`} onClick={() => setToDelete(p)} className="p-2 rounded-md text-[#475569] hover:bg-[#FEF2F2] hover:text-[#991B1B]" title="Eliminar">
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="proveedor-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
              {editing ? "Editar proveedor" : "Nuevo proveedor"}
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Datos de contacto y condiciones comerciales del proveedor.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Nombre</Label>
              <Input data-testid="proveedor-nombre" value={form.nombre} onChange={set("nombre")} required placeholder="Plásticos del Pacífico" className="bg-white border-[#E2E8F0]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Email</Label>
                <Input type="email" data-testid="proveedor-email" value={form.email} onChange={set("email")} placeholder="compras@proveedor.cl" className="bg-white border-[#E2E8F0]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Teléfono</Label>
                <Input data-testid="proveedor-telefono" value={form.telefono} onChange={set("telefono")} placeholder="+56 9 ..." className="bg-white border-[#E2E8F0]" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Condiciones de pago</Label>
              <Input data-testid="proveedor-condiciones" value={form.condiciones} onChange={set("condiciones")} placeholder="30 días" className="bg-white border-[#E2E8F0]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Notas</Label>
              <Textarea data-testid="proveedor-notas" value={form.notas} onChange={set("notas")} placeholder="Observaciones..." className="bg-white border-[#E2E8F0]" />
            </div>
            {error && <p data-testid="proveedor-form-error" className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="proveedor-form-cancel">Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="proveedor-form-submit" className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
                {saving ? <CircleNotch size={18} className="animate-spin" /> : editing ? "Guardar" : "Crear proveedor"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(lotesProv)} onOpenChange={(o) => !o && setLotesProv(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="proveedor-lotes-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
              Lotes de {lotesProv?.nombre}
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Lotes asociados a este proveedor, con estado de pago de la factura.
            </DialogDescription>
          </DialogHeader>
          {lotesData === null ? (
            <div className="flex items-center justify-center py-12"><CircleNotch size={26} className="animate-spin text-[#0F172A]" /></div>
          ) : lotesData.length === 0 ? (
            <p className="py-10 text-center text-[#64748B]">Sin lotes.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="proveedor-lotes-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                    <th className="px-4 py-2.5 font-semibold">Nº lote</th>
                    <th className="px-4 py-2.5 font-semibold">Producto</th>
                    <th className="px-4 py-2.5 font-semibold">Factura</th>
                    <th className="px-4 py-2.5 font-semibold">Pago</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {lotesData.map((l) => (
                    <tr key={l.id} className="border-t border-[#E2E8F0]">
                      <td className="px-4 py-2.5 font-semibold text-[#0F172A]">{l.numero_lote}</td>
                      <td className="px-4 py-2.5 text-[#475569]">{l.producto_nombre}</td>
                      <td className="px-4 py-2.5 text-[#475569]">{l.numero_factura || "—"}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded border ${
                          l.estado_pago === "pagado" ? "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]"
                          : l.estado_pago === "abono" ? "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]"
                          : "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]"}`}>
                          {l.estado_pago === "pagado" ? "Pagado" : l.estado_pago === "abono" ? "Abono" : "Pendiente"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold text-[#0F172A]">{fmtMoney(l.saldo, l.moneda)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-proveedor-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este proveedor?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{toDelete?.nombre}" de la agenda. Los lotes ya creados conservarán el nombre registrado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-proveedor-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-proveedor-confirm" onClick={handleDelete} className="bg-[#DC2626] hover:bg-[#991B1B]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
